import { CHART_TYPES, ENTITY_DIMENSIONS } from './chartTypes'

export function buildConfig(chartType, slotValues, queryInfo) {
  const chartDef = CHART_TYPES[chartType]
  if (!chartDef) return { dimensions: [], metrics: [], chartOptions: {} }

  // Collect dimensions via chart type definition
  const dimensions = chartDef.collectDimensions(slotValues)

  // Collect metrics from slots
  const metricIds = []
  const rawMetrics = slotValues.metrics
  if (Array.isArray(rawMetrics)) {
    rawMetrics.forEach(id => metricIds.push(id))
  } else if (rawMetrics) {
    metricIds.push(rawMetrics)
  }

  // Build columns for dimensionInfo resolution
  const columns = []
  dimensions.forEach(dim => {
    const dimInfo = queryInfo.availableDimensions.find(d => d.id === dim.id)
    const camelId = _.camelCase(dim.id)
    const entity = ENTITY_DIMENSIONS[dim.id]
    if (entity) {
      columns.push({ id: camelId, source: entity.source, label: entity.label })
    } else if (dimInfo?.kind === 'time') {
      columns.push({ id: camelId, source: camelId, kind: 'date', label: dimInfo.label || 'Time' })
    } else if (dimInfo?.options) {
      // Dimensions with options (e.g. kind, campaign_type) — use dot notation
      // so resolveValue looks up the label from dimensionInfo
      columns.push({ id: camelId, source: `${camelId}.name`, label: dimInfo?.label || dim.id })
    } else {
      columns.push({ id: camelId, source: camelId, label: dimInfo?.name || dimInfo?.label || dim.id })
    }
  })

  const metrics = metricIds.map(id => ({ id }))
  metrics.forEach(m => {
    const info = queryInfo.availableMetrics.find(am => am.id === m.id)
    const camelId = _.camelCase(m.id)
    columns.push({ id: camelId, source: camelId, label: info?.label || m.id, kind: 'number' })
  })

  // Build chart options: common columns + chart-type-specific options
  const chartOptions = {
    columns,
    ...chartDef.buildChartOptions(slotValues, metricIds, queryInfo),
  }

  return { dimensions, metrics, chartOptions }
}

export function restoreSlots(config) {
  if (!config) return {}

  const chartDef = CHART_TYPES[config.chartType]
  if (!chartDef) return {}

  const slots = {}
  const dims = config.dimensions || []
  const metricIds = config.metrics?.map(m => m.id) || []
  const assignedDimIds = new Set()

  // Restore each slot based on its kind
  for (const [key, slot] of Object.entries(chartDef.slots)) {
    if (slot.kind === 'time') {
      const dim = dims.find(d => d.timeBucket) || null
      slots[key] = dim
      if (dim) assignedDimIds.add(dim.id)
    } else if (slot.kind === 'metric') {
      slots[key] = slot.single ? metricIds[0] || null : metricIds
    } else if (slot.kind === 'boolean') {
      slots[key] = config.chartOptions?.[key] ?? false
    } else if (slot.kind === 'html') {
      slots[key] = config.chartOptions?.[key] ?? ''
    } else if (slot.kind === 'geoDimension') {
      const dim = dims.find(d => !assignedDimIds.has(d.id))
      slots[key] = dim || null
      if (dim) assignedDimIds.add(dim.id)
    } else if (slot.kind === 'dimension') {
      const available = dims.filter(d => !assignedDimIds.has(d.id))
      if (slot.single) {
        const dim = available[0] || null
        slots[key] = dim
        if (dim) assignedDimIds.add(dim.id)
      } else {
        slots[key] = available
        available.forEach(d => assignedDimIds.add(d.id))
      }
    }
  }

  return slots
}
